import React from 'react';
import { useAdminPanel } from '../hooks/useAdminPanel';
import { useAgentPanel } from '../hooks/useAgentPanel';
import PromptsList from './PromptsList';
import PromptForm from './PromptForm';
import AgentsList from './AgentsList';
import AgentForm from './AgentForm';
import ResponsesMonitor from './ResponsesMonitor';
import ConversationsList from './ConversationsList';
import { CreateAgentRequest, UpdateAgentRequest } from '../types/agent';

type AdminSection = 'conversations' | 'prompts' | 'agents' | 'responses';

interface AdminPanelProps {
  onLoadChat?: (phone: string) => void;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ onLoadChat }) => {
  const [section, setSection] = React.useState<AdminSection>('conversations');
  
  const { 
    activeTab, 
    selectedPrompt, 
    prompts, 
    loading: promptsLoading, 
    error: promptsError,
    actions: promptActions 
  } = useAdminPanel();

  const {
    activeTab: agentTab,
    selectedAgent,
    agents,
    prompts: agentPrompts,
    loading: agentsLoading,
    error: agentsError,
    actions: agentActions
  } = useAgentPanel();

  const handleCreateAgent = async (data: CreateAgentRequest | UpdateAgentRequest) => {
    await agentActions.createAgent(data as CreateAgentRequest);
  };

  const handleUpdateAgent = async (data: CreateAgentRequest | UpdateAgentRequest) => {
    if (!selectedAgent) return;
    await agentActions.updateAgent(selectedAgent.id, data as UpdateAgentRequest);
  };

  const renderPromptsContent = () => {
    switch (activeTab) {
      case 'list':
        return (
          <PromptsList
            prompts={prompts}
            onSelect={promptActions.selectPrompt}
            onDuplicate={promptActions.duplicatePrompt}
            onDelete={promptActions.deletePrompt}
            loading={promptsLoading}
          />
        );
      case 'create':
        return (
          <PromptForm
            onSubmit={promptActions.createPrompt}
            onCancel={() => promptActions.setActiveTab('list')}
            loading={promptsLoading}
          />
        );
      case 'edit':
        return selectedPrompt ? (
          <PromptForm
            prompt={selectedPrompt}
            onSubmit={(data) => promptActions.updatePrompt(selectedPrompt.id, data)}
            onCancel={() => promptActions.setActiveTab('list')}
            loading={promptsLoading}
          />
        ) : null;
      default:
        return null;
    }
  };

  const renderAgentsContent = () => {
    switch (agentTab) {
      case 'list':
        return (
          <AgentsList
            agents={agents}
            onSelect={agentActions.selectAgent}
            onDelete={agentActions.deleteAgent}
            loading={agentsLoading}
          />
        );
      case 'create':
        return (
          <AgentForm
            prompts={agentPrompts}
            onSubmit={handleCreateAgent}
            onCancel={() => agentActions.setActiveTab('list')}
            loading={agentsLoading}
          />
        );
      case 'edit':
        return selectedAgent ? (
          <AgentForm
            agent={selectedAgent}
            prompts={agentPrompts}
            onSubmit={handleUpdateAgent}
            onCancel={() => agentActions.setActiveTab('list')}
            loading={agentsLoading}
          />
        ) : null;
      default:
        return null;
    }
  };

  const handleLoadChat = (phone: string) => {
    if (onLoadChat) {
      onLoadChat(phone);
    }
  };

  return (
    <div className="admin-panel">
      <div className="admin-header-top">
        <h2>Panel de Administración</h2>
        <div className="admin-sections">
          <button
            className={`section-btn ${section === 'conversations' ? 'active' : ''}`}
            onClick={() => setSection('conversations')}
          >
            Conversaciones
          </button>
          <button
            className={`section-btn ${section === 'prompts' ? 'active' : ''}`}
            onClick={() => setSection('prompts')}
          >
            Prompts
          </button>
          <button
            className={`section-btn ${section === 'agents' ? 'active' : ''}`}
            onClick={() => setSection('agents')}
          >
            Agentes
          </button>
          <button
            className={`section-btn ${section === 'responses' ? 'active' : ''}`}
            onClick={() => setSection('responses')}
          >
            Respuestas
          </button>
        </div>
      </div>

      {section === 'conversations' && (
        <div className="admin-content">
          <ConversationsList onLoadChat={handleLoadChat} />
        </div>
      )}

      {section === 'prompts' && (
        <>
          <div className="admin-subheader">
            <h3>Gestión de Prompts</h3>
            <div className="admin-tabs">
              <button
                className={`tab ${activeTab === 'list' ? 'active' : ''}`}
                onClick={() => promptActions.setActiveTab('list')}
              >
                Lista
              </button>
              <button
                className={`tab ${activeTab === 'create' ? 'active' : ''}`}
                onClick={() => promptActions.setActiveTab('create')}
              >
                Crear
              </button>
            </div>
          </div>

          {promptsError && (
            <div className="error-banner">
              <span>{promptsError}</span>
              <button onClick={promptActions.clearError}>×</button>
            </div>
          )}

          <div className="admin-content">
            {renderPromptsContent()}
          </div>
        </>
      )}

      {section === 'agents' && (
        <>
          <div className="admin-subheader">
            <h3>Gestión de Agentes</h3>
            <div className="admin-tabs">
              <button
                className={`tab ${agentTab === 'list' ? 'active' : ''}`}
                onClick={() => agentActions.setActiveTab('list')}
              >
                Lista
              </button>
              <button
                className={`tab ${agentTab === 'create' ? 'active' : ''}`}
                onClick={() => agentActions.setActiveTab('create')}
              >
                Crear
              </button>
            </div>
          </div>

          {agentsError && (
            <div className="error-banner">
              <span>{agentsError}</span>
              <button onClick={agentActions.clearError}>×</button>
            </div>
          )}

          <div className="admin-content">
            {renderAgentsContent()}
          </div>
        </>
      )}

      {section === 'responses' && (
        <>
          <div className="admin-subheader">
            <h3>Monitor de Respuestas IA</h3>
          </div>

          <div className="admin-content">
            <ResponsesMonitor />
          </div>
        </>
      )}

      <style>{`
        .admin-panel {
          background: white;
          border-radius: 8px;
          padding: 20px;
          margin: 20px 0;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          height: calc(100vh - 120px);
          display: flex;
          flex-direction: column;
        }

        .admin-header-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          border-bottom: 1px solid #e0e0e0;
          padding-bottom: 15px;
          flex-shrink: 0;
        }

        .admin-header-top h2 {
          margin: 0;
          color: #333;
          font-size: 24px;
        }

        .admin-sections {
          display: flex;
          gap: 10px;
        }

        .section-btn {
          padding: 10px 20px;
          border: 2px solid #ddd;
          background: white;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
          font-weight: 500;
        }

        .section-btn:hover {
          background: #f5f5f5;
        }

        .section-btn.active {
          background: #2196F3;
          color: white;
          border-color: #2196F3;
        }

        .admin-subheader {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
          flex-shrink: 0;
        }

        .admin-subheader h3 {
          margin: 0;
          color: #555;
          font-size: 20px;
        }

        .admin-tabs {
          display: flex;
          gap: 10px;
        }

        .tab {
          padding: 8px 16px;
          border: 1px solid #ddd;
          background: white;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .tab:hover {
          background: #f5f5f5;
        }

        .tab.active {
          background: #4CAF50;
          color: white;
          border-color: #4CAF50;
        }

        .error-banner {
          background: #ffebee;
          color: #c62828;
          padding: 12px;
          border-radius: 4px;
          margin-bottom: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-shrink: 0;
        }

        .error-banner button {
          background: none;
          border: none;
          color: #c62828;
          font-size: 18px;
          cursor: pointer;
          padding: 0;
          width: 20px;
          height: 20px;
        }

        .admin-content {
          flex: 1;
          overflow-y: auto;
          min-height: 0;
        }
      `}</style>
    </div>
  );
};

export default AdminPanel;
